import { useCallback } from 'react';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { FormattedTextField, DateFieldWithPrefix, createTruncatedTextFormat } from 'framework/ui/field';
import { DATE_FORMAT } from 'framework/utils';
import { PlusIcon } from 'framework/ui/mui-builder';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import UpdateCriticalPeriodButton from './UpdateCriticalPeriodButton';
import {
  Filters,
  DateRangeFilter,
  FiltersLayout,
  EditableListDataGridLayout,
  EDITABLE_LIST_FIELD,
} from 'framework/list';
import { useProjectSecurityContext } from '../../context/ProjectSecurityContext';
import { useNewProjectUrl } from '../../router';
import { ACCESS_INFO_ACTIONS, ACCESS_INFO_SECTIONS } from '../../constants';
import ProjectSectionAccordion from '../../ProjectSectionAccordion';
import { useProjectDataContext } from '../../context/ProjectDataContext';
import CalendarPrefixIcon from '../field/CalendarPrefixIcon';

const ProjectCriticalPeriodsList = () => {
  const { data } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();
  const navigate = useNavigate();
  const { getCreateCriticalPeriodUrl } = useNewProjectUrl();

  const filters = (
    <Filters>
      <FiltersLayout>
        <DateRangeFilter canBeInFuture source="startDate" label="Start Date" />
        <DateRangeFilter canBeInFuture source="endDate" label="End Date" />
      </FiltersLayout>
    </Filters>
  );

  const renderActions = useCallback(() => {
    if (
      isAccessGranted(ACCESS_INFO_SECTIONS.CRITICAL_PERIOD, ACCESS_INFO_ACTIONS.MANAGE) ||
      isAccessGranted(ACCESS_INFO_SECTIONS.CRITICAL_PERIOD, ACCESS_INFO_ACTIONS.PARTIAL_MANAGE)
    ) {
      return [
        <IconButton sx={{ p: 0, mr: 1 }} key="add" onClick={() => navigate(getCreateCriticalPeriodUrl(data.id))}>
          <PlusIcon />
        </IconButton>,
      ];
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccessGranted]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Grid container direction="row" justifyContent="space-between" alignItems="center" pl={2} width="100%">
          <Grid size={2.3}>
            <Typography variant="h4" color="primary">
              Project critical periods
            </Typography>
          </Grid>
          <Grid
            size="auto"
            alignItems="center"
            sx={{
              '& .add-filter-button__wrapper': { m: 0, mb: 1, mr: 1.5 },
              '& .filter-item-wrapper:last-child': { mr: 0 },
              '.filter-item-wrapper': { mb: 0 },
              '.filters-reset-btn': { mb: 0 },
            }}
          >
            {filters}
          </Grid>
          <Grid size="grow" />
          <Grid size="auto" display="flex" alignItems="center">
            {renderActions()}
          </Grid>
        </Grid>
      }
    >
      <Box sx={{ overflowX: 'auto' }}>
        <Box minWidth="1000px">
          <EditableListDataGridLayout
            addNewText="Add period"
            getRowEnabled={() => true}
            rowProps={{ alignItems: 'center' }}
            getTableCells={async () =>
              Promise.resolve([
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'title',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    format: createTruncatedTextFormat(40),
                  },
                  gridCellProps: {
                    size: 3,
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'startDate',
                  isEditor: false,
                  component: DateFieldWithPrefix,
                  componentProps: {
                    tooltipProps: {
                      title: 'Start',
                      children: <CalendarPrefixIcon />,
                    },
                    dateFormat: DATE_FORMAT.FULL_DATE,
                  },
                  gridCellProps: {
                    size: 1.5,
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'endDate',
                  isEditor: false,
                  component: DateFieldWithPrefix,
                  componentProps: {
                    tooltipProps: {
                      title: 'End',
                      children: <CalendarPrefixIcon />,
                    },
                    dateFormat: DATE_FORMAT.FULL_DATE,
                  },
                  gridCellProps: {
                    size: 1.5,
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'description',
                  isEditor: false,
                  component: FormattedTextField,
                  componentProps: {
                    format: createTruncatedTextFormat(80),
                  },
                  gridCellProps: {
                    size: 5,
                  },
                },
                {
                  type: EDITABLE_LIST_FIELD,
                  source: 'id',
                  isEditor: false,
                  component: UpdateCriticalPeriodButton,
                  componentProps: {},
                  gridCellProps: {
                    size: 1,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  },
                },
              ])
            }
          />
        </Box>
      </Box>
    </ProjectSectionAccordion>
  );
};

export default ProjectCriticalPeriodsList;
